import { useEffect, useRef } from "react";

/**
 * Visibility-aware polling helper.
 *
 *  - Calls `fn` immediately and every `intervalMs` while the document is visible.
 *  - When the tab is hidden, polling pauses to save bandwidth and API load.
 *  - When the tab becomes visible again, `fn` is invoked once and the loop resumes.
 *
 * Returns a `forceRefresh` callback you can call after user actions (e.g. after
 * a successful vote) to trigger an immediate refresh without waiting.
 */
export default function useVisibilityAwarePolling(fn, intervalMs = 12000, enabled = true) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);
  const isMountedRef = useRef(true);
  fnRef.current = fn;

  useEffect(() => {
    isMountedRef.current = true;
    if (!enabled) return undefined;

    const clear = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const start = () => {
      clear();
      timerRef.current = setInterval(() => {
        if (document.visibilityState === "visible" && isMountedRef.current) {
          fnRef.current?.();
        }
      }, intervalMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Refresh immediately when returning to the tab, then resume interval
        fnRef.current?.();
        start();
      } else {
        clear();
      }
    };

    // Initial call + interval
    fnRef.current?.();
    start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      isMountedRef.current = false;
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs, enabled]);
}
